import React, { useEffect, useMemo, useRef, useState } from "react";
import Globe, { type GlobeInstance } from "globe.gl";
import { LocateFixed, Minus, Pause, Play, Plus } from "lucide-react";
import { type GlobalServiceNode } from "../data/operationsNexus";

interface GlobalRelationship {
  id: string;
  source: string;
  target: string;
  label: string;
  evidenceStatus: string;
}

interface GlobeViewProps {
  nodes: GlobalServiceNode[];
  relationships: GlobalRelationship[];
  selectedId: string;
  onSelect: (id: string) => void;
}

const globeColors: Record<GlobalServiceNode["type"], string> = {
  "Operations Hub": "#f4f7f9",
  Mobility: "#61a9ff",
  Moving: "#65d3b8",
  Housing: "#a7b8ff",
  Mall: "#dfb777",
  "Partner Network": "#cf9cff",
};

interface PointDatum extends GlobalServiceNode { color: string; radius: number; altitude: number }
interface ArcDatum { id: string; sourceId: string; targetId: string; startLat: number; startLng: number; endLat: number; endLng: number; color: [string, string]; stroke: number; label: string }
interface RingDatum { id: string; lat: number; lng: number; color: string; maxRadius: number; speed: number; period: number }

export const GlobeView: React.FC<GlobeViewProps> = ({ nodes, relationships, selectedId, onSelect }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const globeRef = useRef<GlobeInstance | null>(null);
  const onSelectRef = useRef(onSelect);
  const [rotating, setRotating] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const nodeMap = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);

  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  const points = useMemo<PointDatum[]>(() => nodes.map((node) => ({
    ...node,
    color: selectedId === node.id ? "#ffffff" : globeColors[node.type],
    radius: selectedId === node.id ? 0.72 : node.type === "Operations Hub" ? 0.56 : 0.4,
    altitude: selectedId === node.id ? 0.055 : 0.018,
  })), [nodes, selectedId]);

  const arcs = useMemo<ArcDatum[]>(() => relationships.flatMap((relationship) => {
    const source = nodeMap.get(relationship.source);
    const target = nodeMap.get(relationship.target);
    if (!source || !target) return [];
    const focused = relationship.source === selectedId || relationship.target === selectedId;
    return [{
      id: relationship.id, sourceId: source.id, targetId: target.id,
      startLat: source.lat, startLng: source.lon, endLat: target.lat, endLng: target.lon,
      color: focused ? ["#ffffff", globeColors[target.type]] : ["rgba(101,211,184,.62)", "rgba(97,169,255,.12)"],
      stroke: focused ? 1.35 : 0.52,
      label: `${relationship.label}: ${source.name} → ${target.name} · ${relationship.evidenceStatus}`,
    }];
  }), [relationships, nodeMap, selectedId]);

  const rings = useMemo<RingDatum[]>(() => nodes.slice(1).map((node) => ({
    id: node.id, lat: node.lat, lng: node.lon,
    color: selectedId === node.id ? "#ffffff" : globeColors[node.type],
    maxRadius: selectedId === node.id ? 4.8 : 2.7, speed: selectedId === node.id ? 2.1 : 0.8, period: selectedId === node.id ? 900 : 2200,
  })), [nodes, selectedId]);

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.innerHTML = "";
    let globe: GlobeInstance | null = null;
    try {
      const createdGlobe = new (Globe as unknown as new (element: HTMLElement) => GlobeInstance)(containerRef.current);
      globe = createdGlobe;
      createdGlobe
        .globeImageUrl("//unpkg.com/three-globe/example/img/earth-night.jpg")
        .bumpImageUrl("//unpkg.com/three-globe/example/img/earth-topology.png")
        .backgroundColor("rgba(0,0,0,0)").showAtmosphere(true).atmosphereColor("#315f69").atmosphereAltitude(0.16)
        .pointsData([]).pointLat("lat").pointLng("lon").pointColor("color").pointRadius("radius").pointAltitude("altitude")
        .pointLabel((datum: object) => { const point = datum as PointDatum; return `<div class="globe-tooltip"><span>${point.type}</span><strong>${point.name}</strong><small>${point.location}</small><em>${point.evidenceStatus} DATA</em></div>`; })
        .onPointClick((datum: object) => onSelectRef.current((datum as PointDatum).id))
        .arcsData([]).arcStartLat("startLat").arcStartLng("startLng").arcEndLat("endLat").arcEndLng("endLng")
        .arcColor("color").arcStroke("stroke").arcDashLength(0.62).arcDashGap(0.28).arcDashAnimateTime(5200).arcAltitudeAutoScale(0.24)
        .arcLabel("label").onArcClick((datum: object) => onSelectRef.current((datum as ArcDatum).targetId))
        .ringsData([]).ringLat("lat").ringLng("lng").ringColor("color").ringMaxRadius("maxRadius").ringPropagationSpeed("speed").ringRepeatPeriod("period");
      setInitError(null);
    } catch (error) {
      globe?._destructor();
      if (containerRef.current) containerRef.current.innerHTML = "";
      console.error("FTNERS Nexus globe initialization failed", error);
      setInitError("3D network could not initialize in this browser.");
      return;
    }

    globe.pointOfView({ lat: 24, lng: 116, altitude: 2.05 }, 900);
    const controls = globe.controls();
    controls.autoRotate = true; controls.autoRotateSpeed = 0.2; controls.enableDamping = true; controls.dampingFactor = 0.08;
    globeRef.current = globe;
    const resize = () => { if (containerRef.current && globeRef.current) globeRef.current.width(containerRef.current.clientWidth).height(containerRef.current.clientHeight); };
    resize(); window.addEventListener("resize", resize);
    return () => { window.removeEventListener("resize", resize); globeRef.current = null; globe?._destructor(); if (containerRef.current) containerRef.current.innerHTML = ""; };
  }, []);

  useEffect(() => { globeRef.current?.pointsData(points).arcsData(arcs).ringsData(rings); }, [points, arcs, rings]);
  useEffect(() => { const selected = nodeMap.get(selectedId); if (selected) globeRef.current?.pointOfView({ lat: selected.lat, lng: selected.lon, altitude: 1.55 }, 1100); }, [selectedId, nodeMap]);

  const toggleRotation = () => { const controls = globeRef.current?.controls(); if (!controls) return; controls.autoRotate = !rotating; setRotating(!rotating); };
  const changeZoom = (delta: number) => { const globe = globeRef.current; if (!globe) return; const current = globe.pointOfView(); globe.pointOfView({ altitude: Math.min(3.4, Math.max(0.75, current.altitude + delta)) }, 350); };

  return (
    <div className="globe-wrap">
      <div ref={containerRef} className="globe-canvas" />
      {initError && <div className="globe-fallback" role="status"><span>GLOBAL SERVICE NETWORK · 2D FALLBACK</span><strong>{initError}</strong><p>The case list, relationship inspector and source context remain available.</p><div>{nodes.map((node) => <button key={node.id} onClick={() => onSelect(node.id)}>{node.name}<small>{node.evidenceStatus}</small></button>)}</div></div>}
      <div className="globe-controls">
        <button onClick={toggleRotation} aria-label={rotating ? "Pause earth rotation" : "Play earth rotation"}>{rotating ? <Pause size={14} /> : <Play size={14} />}</button>
        <button onClick={() => changeZoom(-0.3)} aria-label="Zoom in"><Plus size={14} /></button>
        <button onClick={() => changeZoom(0.3)} aria-label="Zoom out"><Minus size={14} /></button>
        <button onClick={() => globeRef.current?.pointOfView({ lat: 24, lng: 116, altitude: 2.05 }, 800)} aria-label="Reset camera"><LocateFixed size={14} /></button>
      </div>
    </div>
  );
};
